/**
 * usePdfViewer — stato globale del visualizzatore PDF in overlay (FE v2.174.0)
 *
 * @description Singleton a livello di modulo: `PdfViewerDialog` (montato una
 *              volta in App.vue) legge `state`, `usePdfDownload` chiama
 *              `show()`. Usato nella PWA standalone su iOS, dove un PDF aperto
 *              in una nuova finestra occupa tutto lo schermo senza Indietro né
 *              Condividi.
 *
 *              `show()` apre subito l'overlay in caricamento e poi scarica il
 *              blob. Un contatore scarta le risposte arrivate dopo una
 *              chiusura o dopo l'apertura di un altro documento.
 */

import { shallowReactive } from 'vue'

export interface PdfViewerLoadResult {
  blob: Blob
  /** Nome dal Content-Disposition del BE; se assente resta quello della richiesta */
  fileName?: string | null
}

export interface PdfViewerRequest {
  /** Titolo nella barra (es. "Preventivo 10439") */
  title: string
  /** Nome di riserva del file condiviso o scaricato (es. "Preventivo_10439.pdf") */
  fileName: string
  /** Scarica il PDF (Bearer via axios) */
  load: () => Promise<PdfViewerLoadResult>
}

export interface PdfViewerState {
  open: boolean
  loading: boolean
  title: string
  fileName: string
  blob: Blob | null
  error: string | null
}

const state = shallowReactive<PdfViewerState>({
  open: false,
  loading: false,
  title: '',
  fileName: 'documento.pdf',
  blob: null,
  error: null,
})

let requestSeq = 0

async function show(request: PdfViewerRequest): Promise<void> {
  const seq = ++requestSeq
  Object.assign(state, {
    open: true,
    loading: true,
    title: request.title,
    fileName: request.fileName,
    blob: null,
    error: null,
  })

  try {
    const result = await request.load()
    if (seq !== requestSeq) return
    if (result.fileName) state.fileName = result.fileName
    state.blob = result.blob
  } catch {
    if (seq !== requestSeq) return
    state.error = 'Impossibile aprire il PDF (documento inesistente o permessi insufficienti)'
  } finally {
    if (seq === requestSeq) state.loading = false
  }
}

function close(): void {
  requestSeq++
  Object.assign(state, { open: false, loading: false, blob: null, error: null })
}

export function usePdfViewer() {
  return {
    state: state as Readonly<PdfViewerState>,
    show,
    close,
  }
}
